/**
 * AUTH SLICE — Gestisce l'autenticazione utente.
 *
 * Gli endpoint /user/login, /user/refresh, /user/resetPassword sono
 * anonimi (no Authorization header). Il backend gestisce OAuth2
 * interamente server-side. Gli endpoint utente-scoped (/user/init,
 * /user/logout) usano il Bearer utente restituito da /user/login.
 *
 * Storage token: tokenManager module-level per l'accessToken (volatile),
 * sessionStorage per refreshToken + webId (persiste al reload, perso
 * alla chiusura tab). Mitigazione XSS: niente accessToken in storage.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CRM_BASE_URL } from '../api/config';
import { httpClient } from '../api/httpClient';
import { extractError } from '../store/thunkHelpers';
import { performRefresh } from './refreshAuth';
import { requestSessionFromOtherTabs } from './sessionHandshake';
import {
  adoptSharedSession,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getWebId,
  setTokens,
} from './tokenManager';
import type {
  AuthState,
  ChangePasswordPayload,
  ChangeRequiredPasswordPayload,
  LoginCredentials,
  LoginRejectPayload,
  LoginResponse,
  ResetPasswordPayload,
  SetPasswordByTicketPayload,
  UpdateProfilePayload,
  User,
  UserSettings,
} from './types';

const initialState: AuthState = {
  user: null,
  webId: null,
  status: 'idle',
  error: null,
  requiresWebId: false,
  userSettings: null,
};

export const login = createAsyncThunk<
  LoginResponse & { webId: string },
  LoginCredentials,
  { rejectValue: LoginRejectPayload }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch(`${CRM_BASE_URL}/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null) as { title?: string } | null;
        const message = errorBody?.title ?? `Login fallito (${response.status})`;
        // Status passato esplicitamente nel payload del reject: `extraReducers`
        // sotto lo usa per discriminare il 412 (vedi `AuthState.requiresWebId`).
        return rejectWithValue({ message, status: response.status });
      }
      const loginResponse = (await response.json()) as LoginResponse;
      if (!loginResponse.user) {
        return rejectWithValue({ message: 'Login fallito: nessun utente nella risposta' });
      }
      // apiHost: salvato qui (in setTokens) — httpClient lo legge per riscrivere
      // l'host di tutte le chiamate authed verso la company corretta. Se assente
      // (backend vecchio o mock), il client resta sull'host di build.
      setTokens({
        accessToken: loginResponse.accessToken,
        refreshToken: loginResponse.refreshToken,
        webId: credentials.webId,
        apiHost: loginResponse.apiHost,
      });
      return { ...loginResponse, webId: credentials.webId };
    } catch (err) {
      // Errore di rete o di parsing: nessuno status HTTP disponibile.
      return rejectWithValue({ message: err instanceof Error ? err.message : 'Errore login' });
    }
  }
);

export const refreshAccessToken = createAsyncThunk<LoginResponse>(
  'auth/refresh',
  async (_, { rejectWithValue }) => {
    try {
      // Delega a performRefresh: stessa singleton in-flight usata dal 401
      // interceptor di httpClient, cosi' un refresh in volo non ne triggera
      // un secondo anche se partono da code path diversi.
      return await performRefresh();
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Errore refresh');
    }
  }
);

export const checkAuth = createAsyncThunk<LoginResponse & { webId: string }>(
  'auth/check',
  async (_, { dispatch, rejectWithValue }) => {
    let refreshToken = getRefreshToken();
    let webId = getWebId();

    // Nessuna sessione locale (es. link della mail aperto in una nuova tab):
    // provo a farmela passare da una tab già autenticata via BroadcastChannel.
    // Se qualcuno risponde entro il timeout, proseguo col normale refresh.
    if (!refreshToken || !webId) {
      const shared = await requestSessionFromOtherTabs();
      if (shared) {
        adoptSharedSession(shared);
        refreshToken = getRefreshToken();
        webId = getWebId();
      }
    }

    if (!refreshToken || !webId) {
      return rejectWithValue({ reason: 'no_session' });
    }
    try {
      const refreshResult = await dispatch(refreshAccessToken()).unwrap();
      // Via httpClient: e' authed (Bearer), quindi deve atterrare sull'host
      // della company giusta — il client riscrive su apiHost se settato.
      const initResponse = await httpClient.get<User>('/user/init');
      const user = initResponse.data;
      return { ...refreshResult, user, webId };
    } catch (err) {
      clearTokens();
      return rejectWithValue({
        reason: 'refresh_failed',
        error: err instanceof Error ? err.message : 'Sessione scaduta',
      });
    }
  }
);

export const updateProfile = createAsyncThunk<
  User,
  UpdateProfilePayload,
  { rejectValue: string }
>('auth/updateProfile', async (payload, { rejectWithValue }) => {
  try {
    // Endpoint user-scoped (Bearer) -> httpClient. Ritorna lo User aggiornato
    // che rimpiazza state.auth.user (nome/cognome/email/lingua/portrait/fullName).
    const response = await httpClient.patch<User>('/user/profile', payload);
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Aggiorna il logo aziendale (PATCH /company/logo, solo admin lato backend).
 * Payload: immagine in base64 SENZA prefisso data URL. Ritorna il nuovo
 * `logoURL`, che il reducer scrive in `state.user.companyLogoURL` -> l'header si
 * aggiorna da solo (legge lo stesso campo).
 */
export const updateCompanyLogo = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('auth/updateCompanyLogo', async (logoFile, { rejectWithValue }) => {
  try {
    const response = await httpClient.patch<{ logoURL: string }>('/company/logo', { logoFile });
    return response.data.logoURL;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Rinomina l'istanza (PATCH /company/name, solo admin lato backend). Ritorna il nome
 * salvato, che il reducer scrive in `state.user.companyName`: l'header lo legge da lì.
 */
export const updateCompanyName = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('auth/updateCompanyName', async (name, { rejectWithValue }) => {
  try {
    const response = await httpClient.patch<{ name: string }>('/company/name', { name });
    return response.data.name;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

/** Ripristina il logo di default (DELETE /company/logo). Ritorna `logoURL` vuoto. */
export const resetCompanyLogo = createAsyncThunk<
  string,
  void,
  { rejectValue: string }
>('auth/resetCompanyLogo', async (_, { rejectWithValue }) => {
  try {
    const response = await httpClient.delete<{ logoURL: string }>('/company/logo');
    return response.data.logoURL;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

export const updatePassword = createAsyncThunk<
  void,
  ChangePasswordPayload,
  { rejectValue: string }
>('auth/updatePassword', async (payload, { rejectWithValue }) => {
  try {
    // Endpoint user-scoped (Bearer): il backend verifica la password corrente e
    // applica la nuova. Nessuno stato Redux da aggiornare (no extraReducer):
    // i token restano validi, l'utente non cambia.
    await httpClient.post('/user/updatePassword', payload);
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Cambio password OBBLIGATORIO (flag `passwordReset` di Liferay).
 *
 * Endpoint ANONIMO, come /user/login: niente Authorization header. Non è una svista — con
 * quel flag alzato Liferay tratta l'utente come un ospite e respinge con 403 ogni chiamata
 * autenticata, `/user/updatePassword` compresa. Il token c'è ma non apre nulla, quindi
 * l'unica strada per sbloccarsi passa da un endpoint pubblico che riverifica le credenziali
 * server-side. Per questo il payload porta webId ed emailAddress: senza utente di contesto,
 * il backend non saprebbe di chi si parla.
 */
export const changeRequiredPassword = createAsyncThunk<
  void,
  ChangeRequiredPasswordPayload,
  { rejectValue: string }
>('auth/changeRequiredPassword', async (payload, { rejectWithValue }) => {
  try {
    const response = await fetch(`${CRM_BASE_URL}/user/changeRequiredPassword`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null) as { title?: string } | null;
      return rejectWithValue(errorBody?.title ?? `Cambio password fallito (${response.status})`);
    }
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err.message : 'Errore cambio password');
  }
});

/**
 * Chiede il link di reimpostazione della password (FILO-70). Anonimo come /user/login.
 *
 * Il backend risponde 200 anche per un indirizzo sconosciuto, per non rivelare chi è
 * registrato: un esito positivo vuol dire «richiesta presa in carico», non «email inviata».
 * L'unico errore che l'utente può correggere è l'istanza sconosciuta (404/412), che il
 * rejectValue segnala con `webIdNotFound` perché la pagina mostri il campo Istanza.
 */
export const requestPasswordReset = createAsyncThunk<
  void,
  ResetPasswordPayload,
  { rejectValue: { message: string; webIdNotFound: boolean } }
>('auth/requestPasswordReset', async (payload, { rejectWithValue }) => {
  try {
    const response = await fetch(`${CRM_BASE_URL}/user/resetPassword`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null) as { title?: string } | null;
      return rejectWithValue({
        message: errorBody?.title ?? `Richiesta fallita (${response.status})`,
        webIdNotFound: response.status === 404 || response.status === 412,
      });
    }
  } catch (err) {
    return rejectWithValue({
      message: err instanceof Error ? err.message : 'Errore nella richiesta',
      webIdNotFound: false,
    });
  }
});

/**
 * Imposta la nuova password con il ticket del link ricevuto per email (FILO-70). Anonimo:
 * il ticket prende il posto della password corrente come prova d'identità.
 *
 * Il messaggio d'errore arriva già tradotto dal backend (link non valido o scaduto, password
 * che non rispetta la politica), quindi si mostra così com'è.
 */
export const setPasswordByTicket = createAsyncThunk<
  void,
  SetPasswordByTicketPayload,
  { rejectValue: string }
>('auth/setPasswordByTicket', async (payload, { rejectWithValue }) => {
  try {
    const response = await fetch(`${CRM_BASE_URL}/user/setPasswordByTicket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null) as { title?: string } | null;
      return rejectWithValue(errorBody?.title ?? `Impostazione password fallita (${response.status})`);
    }
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err.message : 'Errore impostazione password');
  }
});

export const fetchUserSettings = createAsyncThunk<
  UserSettings,
  void,
  { rejectValue: string }
>('auth/fetchUserSettings', async (_, { rejectWithValue }) => {
  try {
    const response = await httpClient.get<UserSettings>('/user/settings');
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

export const updateUserSettings = createAsyncThunk<
  UserSettings,
  UserSettings,
  { rejectValue: string }
>('auth/updateUserSettings', async (payload, { rejectWithValue }) => {
  try {
    const response = await httpClient.patch<UserSettings>('/user/settings', payload);
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  if (getAccessToken()) {
    try {
      // Via httpClient: authed (Bearer) -> host della company giusta via apiHost.
      await httpClient.get('/user/logout');
    } catch {
      // best effort: se il logout server fallisce, pulisco comunque la sessione locale
    }
  }
  clearTokens();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'authenticated';
        if (action.payload.user) state.user = action.payload.user;
        state.webId = action.payload.webId;
        // Reset esplicito: dopo un login successful (anche se al precedente
        // tentativo il backend aveva richiesto il webId via 412), il campo
        // Istanza torna nascosto al successivo logout/refresh.
        state.requiresWebId = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'unauthenticated';
        state.error = action.payload?.message ?? 'Login fallito';
        // Status 412 = NoSuchCompanyException Liferay: il webId fornito non
        // risolve a una Company. La LoginPage legge questo flag per rendere
        // visibile il campo "Istanza" anche su hostname testuale.
        state.requiresWebId = action.payload?.status === 412;
      })
      .addCase(checkAuth.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.status = 'authenticated';
        if (action.payload.user) state.user = action.payload.user;
        state.webId = action.payload.webId;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.status = 'unauthenticated';
        state.user = null;
        state.webId = null;
      })
      // refreshAccessToken non ha extraReducer: i token vivono in tokenManager,
      // non in Redux. Il thunk serve solo come canale dispatch-unwrap per
      // checkAuth e altri code path che vogliono orchestrare via Redux.
      // updateProfile: aggiorna SOLO state.user (NON tocca status, altrimenti
      // un transitorio 'loading' farebbe stazionare l'AuthGuard sul loader).
      // pending/rejected sono gestiti dal componente via unwrap + toast.
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      // Logo aziendale: aggiorna solo companyLogoURL sullo user in store, così
      // header e sezione Impostazioni si riallineano senza refetch.
      .addCase(updateCompanyLogo.fulfilled, (state, action) => {
        if (state.user) state.user.companyLogoURL = action.payload;
      })
      .addCase(updateCompanyName.fulfilled, (state, action) => {
        if (state.user) state.user.companyName = action.payload;
      })
      .addCase(resetCompanyLogo.fulfilled, (state, action) => {
        if (state.user) state.user.companyLogoURL = action.payload;
      })
      // Cambiando la password, il backend passa passwordReset=false a Liferay
      // (_userService.updatePassword(..., false)): il flag laggiù si abbassa da
      // solo. Qui lo rispecchiamo nello store, altrimenti l'AuthGuard
      // continuerebbe a rimbalzare l'utente sulla pagina di cambio obbligatorio
      // subito dopo che l'ha cambiata — un ciclo senza uscita fino al reload.
      .addCase(updatePassword.fulfilled, (state) => {
        if (state.user) state.user.passwordReset = false;
      })
      // Stesso motivo del case qui sopra: il cambio obbligatorio abbassa il flag
      // lato Liferay, e senza rispecchiarlo l'AuthGuard rimbalzerebbe l'utente
      // sulla pagina che ha appena completato.
      .addCase(changeRequiredPassword.fulfilled, (state) => {
        if (state.user) state.user.passwordReset = false;
      })
      // fetch/update delle preferenze utente: aggiornano solo state.userSettings
      // (stesso ragionamento di updateProfile: niente status, no AuthGuard loader).
      .addCase(fetchUserSettings.fulfilled, (state, action) => {
        state.userSettings = action.payload;
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.userSettings = action.payload;
      })
      .addCase(logout.fulfilled, () => ({
        ...initialState,
        // initialState.status = 'idle' ma dopo logout sappiamo che l'utente
        // NON e' autenticato — 'idle' farebbe stazionare AuthGuard sul
        // loader "Verifica autenticazione..." invece di redirigere a /login.
        status: 'unauthenticated' as const,
      }));
  },
});

export const authReducer = authSlice.reducer;
